// Copyright ©️ 2026 eVolve MEP, LLC

import { useEffect, useMemo, useState } from 'react';

import { convertTimeFrameDataToRequestParams } from 'components/Mantine/TimeFramePicker/helpers';
import { isNil } from 'helpers/isNotNil';
import { strongEntries, type StrongEntries, strongIsEqual } from 'helpers/strongEntries';
import { useChartFilters, type VDCOSChartFilters } from 'modules/Shop/Insights/RevitUsage/useChartFilters';

const keyMapping: Partial<Record<keyof VDCOSChartFilters, string>> = {
  selectedRevitYear: 'revitVersion',
};

export const useAnalyticsParams = (usedFilters: readonly (keyof VDCOSChartFilters)[]) => {
  const filters = useChartFilters();
  const [filteredParams, setFilteredParams] = useState<StrongEntries<typeof filters>>([]);
  useEffect(() => {
    const newFilteredParams = strongEntries(filters).filter(([key]) => usedFilters.includes(key));
    // Prevent changes to unrelated filters from causing us to rerender
    if (!strongIsEqual(filteredParams, newFilteredParams)) {
      setFilteredParams(newFilteredParams);
    }
  }, [filteredParams, filters, usedFilters]);

  const stillResolving = useMemo(
    () => filteredParams.length === 0 || filteredParams.some(([_, value]) => isNil(value)),
    [filteredParams],
  );

  const params = useMemo(
    () =>
      filteredParams.reduce(
        (out, [key, value]) => {
          if (!value) return out;
          if (key === 'timeFrame') {
            return { ...out, ...(convertTimeFrameDataToRequestParams(value) as Record<string, string | string[]>) };
          }
          const remappedKey = String(keyMapping[key] ?? key);
          return { ...out, [remappedKey]: value };
        },
        {} as Record<string, string | string[] | undefined>,
      ),
    [filteredParams],
  );

  return { params, stillResolving };
};
